import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CcoHttpParameterCodec } from '@app/shared/services/cco-http-parameter-codec';
import { StandardService } from '@app/shared/services/standard.service';
import { Customer } from '@cco/model';
import { isNil, omitBy } from 'lodash';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '@cco/apps/cco-frontend';
import { EntityOp } from '@ngrx/data';
import { ENTITY_SERVICE, EntityService } from '@core/services/entity.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Statistic } from '@main/customers/store/statistic';

export interface CustomersQuery {
  /**
   * op must be 'or' or undefined at present; future support for 'and' may be added
   */
  op?: 'and' | 'or';
  /**
   * search the customer name and associated contact names
   */
  nameContains?: string;
  /**
   * search the customer telephone numbers and associated contact telephone numbers
   */
  telephoneNumber?: string;
  /**
   * search the customer email and associated contact emails
   */
  email?: string;
}

@Injectable()
export class CustomerService extends StandardService<Customer, number> {
  constructor(
    @Inject(APP_CONFIG) config: AppConfig,
    protected http: HttpClient,
    @Inject(ENTITY_SERVICE) private entityService: EntityService,
    private snackBar: MatSnackBar,
  ) {
    super(http, config.apiBaseUrl, `/customers`);
  }

  getSumOfJobAmountsByCalendarYear$(customerId: number): Observable<Statistic[]> {
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http.get<Statistic[]>(`${baseUrl + this.relativeUrl}/${customerId}/statistics/jobs`)),
    );
  }

  query$(query: CustomersQuery): Observable<Customer[]> {
    const params = new HttpParams({
      encoder: new CcoHttpParameterCodec(),
      fromObject: omitBy(query, isNil),
    });
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http
      .get<Customer[]>(`${baseUrl + this.relativeUrl}`, { params })
      .pipe(map(response => this.mapModelObjects.bind(this)(response)))));
  }

  setCustomerAuthorization$(customerId: number, authorized: boolean): Observable<Customer> {
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http.put<Customer>(
          `${ baseUrl }/customers/${ customerId }/authorization`,
          '',
          {
            params: {
              authorized: `${ authorized }`,
            },
          },
        ).pipe(
          catchError(() => {
            this.snackBar.open('An error occurred while updating the Customer authorization.', 'Close');
            return EMPTY;
          }),
        ),
      ),
      tap(customer => this.entityService.getEcs('Customer')
        .createAndDispatch(EntityOp.UPSERT_ONE, customer)),
    );
  }
}
